import type { SupportAttachmentKind } from '../types/support';

/** Макс. вложений в одном сообщении (backend default). */
export const SUPPORT_MAX_FILES = 5;

/** Макс. длина текста (backend default). */
export const SUPPORT_MAX_TEXT_LENGTH = 4000;

const MB = 1024 * 1024;

/** Лимиты размера по kind (backend defaults). */
export const SUPPORT_SIZE_LIMITS: Record<SupportAttachmentKind, number> = {
  image: 10 * MB,
  video: 50 * MB,
  audio: 20 * MB,
  voice: 20 * MB,
  file: 50 * MB,
};

const BLOCKED_EXTENSIONS = new Set([
  'exe',
  'bat',
  'cmd',
  'com',
  'msi',
  'scr',
  'dll',
  'sh',
  'bash',
  'ps1',
  'vbs',
  'js',
  'jar',
  'app',
  'dmg',
]);

const IMAGE_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
]);

const VIDEO_MIMES = new Set(['video/mp4', 'video/webm', 'video/quicktime']);

const AUDIO_MIMES = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/ogg',
  'audio/wav',
  'audio/x-wav',
  'audio/mp4',
  'audio/aac',
  'audio/webm',
]);

const FILE_MIMES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-rar-compressed',
  'application/vnd.rar',
  'application/x-7z-compressed',
  'text/plain',
  'text/csv',
]);

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif']);
const VIDEO_EXT = new Set(['mp4', 'webm', 'mov']);
const AUDIO_EXT = new Set(['mp3', 'ogg', 'wav', 'm4a', 'aac']);
const FILE_EXT = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'zip',
  'rar',
  '7z',
  'csv',
]);

/** Значение атрибута accept для input[type=file]. */
export const SUPPORT_FILE_ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'audio/mp4',
  'audio/aac',
  'audio/webm',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-zip-compressed',
  'text/plain',
  'text/csv',
  '.png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.mov,.mp3,.ogg,.wav,.m4a,.aac,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z,.csv',
].join(',');

export const formatSupportBytes = (size: number): string => {
  if (size < 1024) return `${size} Б`;
  if (size < MB) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / MB).toFixed(1)} МБ`;
};

const getExtension = (name: string): string => {
  const dot = name.lastIndexOf('.');
  if (dot === -1) return '';
  return name.slice(dot + 1).toLowerCase();
};

const kindLabelRu: Record<SupportAttachmentKind, string> = {
  image: 'изображения',
  video: 'видео',
  audio: 'аудио',
  voice: 'голосового',
  file: 'файла',
};

/** Классификация вложения (логика как на backend). */
export function classifySupportFile(file: File): SupportAttachmentKind | null {
  const ext = getExtension(file.name);
  if (ext && BLOCKED_EXTENSIONS.has(ext)) return null;

  const mime = (file.type || '').toLowerCase();

  if (IMAGE_MIMES.has(mime) || IMAGE_EXT.has(ext)) return 'image';
  if (VIDEO_MIMES.has(mime) || VIDEO_EXT.has(ext)) return 'video';
  if (AUDIO_MIMES.has(mime) || AUDIO_EXT.has(ext)) return 'audio';
  if (FILE_MIMES.has(mime) || FILE_EXT.has(ext)) return 'file';

  // audio/webm без video — редкий edge
  if (mime === 'audio/webm') return 'audio';

  return null;
}

/** Определить kind по mime с сервера (fallback для старых записей без kind). */
export function inferAttachmentKind(mimeType: string | null | undefined): SupportAttachmentKind {
  const mime = (mimeType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

/** Старые голосовые из Telegram: kind audio + ogg + voice_* */
export function isLegacyTelegramVoice(att: {
  kind?: SupportAttachmentKind;
  mimeType?: string | null;
  originalName?: string | null;
}): boolean {
  if (att.kind === 'voice') return true;
  const mime = (att.mimeType || '').toLowerCase();
  const name = att.originalName || '';
  return (
    att.kind === 'audio' &&
    mime === 'audio/ogg' &&
    (name.startsWith('voice') || name.startsWith('voice_'))
  );
}

/** Итоговый kind для UI (с учётом legacy). */
export function resolveAttachmentKind(att: {
  kind?: SupportAttachmentKind;
  mimeType?: string | null;
  originalName?: string | null;
}): SupportAttachmentKind {
  if (att.kind === 'voice') return 'voice';
  if (isLegacyTelegramVoice(att)) return 'voice';
  return att.kind ?? inferAttachmentKind(att.mimeType);
}

export function getAttachmentKind(
  att: { kind?: SupportAttachmentKind; mimeType?: string | null; originalName?: string | null },
): SupportAttachmentKind {
  return resolveAttachmentKind(att);
}

/** Голосовое и обычное аудио — плеер, не video. */
export function isAudioPlaybackKind(kind: SupportAttachmentKind): boolean {
  return kind === 'voice' || kind === 'audio';
}

/** Валидация одного файла; null — ок. */
export function validateSupportFile(file: File): string | null {
  const ext = getExtension(file.name);
  if (ext && BLOCKED_EXTENSIONS.has(ext)) {
    return `«${file.name}»: исполняемые файлы не поддерживаются`;
  }

  const kind = classifySupportFile(file);
  if (!kind) {
    return `«${file.name}»: неподдерживаемый тип. Допустимы изображения, видео, аудио и документы (pdf, docx, zip…)`;
  }

  const limit = SUPPORT_SIZE_LIMITS[kind];
  if (file.size > limit) {
    const maxMb = limit / MB;
    return `«${file.name}» слишком большой. Максимум для ${kindLabelRu[kind]}: ${maxMb} МБ`;
  }

  return null;
}
